import {
	DevCycleUser,
	useDevCycleClient,
	useIsDevCycleInitialized,
	useVariableValue
} from "@devcycle/react-client-sdk";
import usePropelAuth from "@/app2/shared/hooks/usePropelAuth";

const useDevCycleSdk = () => ({
	playlists: useVariableValue("playlist", false),
	announcements: useVariableValue("announcements", false),
	archiveUserFlow: useVariableValue("archive-user-flow", true),
	contentCreationUpdates: true,
	hideCreationButtons: useVariableValue("hide-creation-buttons", true),
	filterSettingsPersistence: true,
	teamProgress: true,
	trainingPool: true,
	disableAutoAssign: true,
	meiliSearchIntegration: true,
	bulkRescheduleOverdue: true,
	courseBulkEnrollment: true,
	playlistBasedCerts: useVariableValue("playlist-based-certs", false),
	dueDateAssignments: useVariableValue("due-date-assignments", false),
	monthEndReport: true,
	monthlyReportDistributionList: useVariableValue("monthly-report-distribution-list", false),
	pauseStreak: true,
	lessonLinksAttachments: useVariableValue("lesson-links-attachments", false),
	newHomeScreen: true,
	useFeedEndpoint: useVariableValue("new-feed-logic", false),
	useApp2: true,
	frankAi: useVariableValue("frank-ai", false),
	leaderboards: useVariableValue("use-leaderboards", false)
});

export type DevCycleSdkFlags = ReturnType<typeof useDevCycleSdk>;

/**
 * Flags driven by DevCycle {@link useVariableValue} (excludes hardcoded `true` toggles in this hook).
 * `variableKey` matches the key in the DevCycle dashboard.
 */
export const REMOTE_DEVCYCLE_FLAG_SPECS = [
	{sdkKey: "playlists" as const, variableKey: "playlist"},
	{sdkKey: "announcements" as const, variableKey: "announcements"},
	{sdkKey: "archiveUserFlow" as const, variableKey: "archive-user-flow"},
	{sdkKey: "hideCreationButtons" as const, variableKey: "hide-creation-buttons"},
	{sdkKey: "playlistBasedCerts" as const, variableKey: "playlist-based-certs"},
	{sdkKey: "dueDateAssignments" as const, variableKey: "due-date-assignments"},
	{sdkKey: "monthlyReportDistributionList" as const, variableKey: "monthly-report-distribution-list"},
	{sdkKey: "lessonLinksAttachments" as const, variableKey: "lesson-links-attachments"},
	{sdkKey: "useFeedEndpoint" as const, variableKey: "new-feed-logic"},
	{sdkKey: "useApp2" as const, variableKey: "use-app2"},
	{sdkKey: "frankAi" as const, variableKey: "frank-ai"}
] as const;

/**
 * Enabled remote flags as a subtle debug line, or null if none on.
 * Includes `use-app2` whenever App2 is active, including when forced via `REACT_APP_USE_APP2` at build time.
 */
export function formatEnabledRemoteDevCycleFlags(flags: DevCycleSdkFlags): string | null {
	const enabled: string[] = [];

	for (const {sdkKey, variableKey} of REMOTE_DEVCYCLE_FLAG_SPECS) {
		if (flags[sdkKey]) {
			enabled.push(variableKey);
		}
	}

	if (!enabled.length) {
		return null;
	}

	enabled.sort();
	return `Flags: ${enabled.join(", ")}`;
}

export const useDevCycleIdentifier = (): void => {
	const devCycleClient = useDevCycleClient();
	const isDevCycleInitialized = useIsDevCycleInitialized();
	const {authUser, authInfo} = usePropelAuth();

	if (!authUser || isDevCycleInitialized) return;

	const devCycleUser: DevCycleUser = {
		user_id: authUser.userId ?? "unidentified-user",
		isAnonymous: false,
		email: authUser.email ?? "",
		customData: {
			orgId: authInfo.orgHelper?.getOrgIds()?.[0] ?? ""
		}
	};

	devCycleClient.identifyUser(devCycleUser, error => {
		if (error) console.error("devCycleClient.identifyUser", error);
	});
};

export default useDevCycleSdk;
